import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18next';
import { CurrentUserData, Enlistment } from 'models/user';
import { HourFormat } from 'models/generic';
import { useAppContext } from 'context/hooks';
import { FormatDateProps } from 'models/general';
import { showUsaFeature } from './app';

export const generalDateFormat = showUsaFeature ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

export const formatToUnix = (date: Dayjs) => (date.unix() * 1000).toString();

export const formatToIso = (timestampInMilliSeconds: string) => new Date(Number(timestampInMilliSeconds)).toISOString();

export const calculateDaysLeft = (date: Date) => dayjs(date).diff(new Date(), 'day');

export const calculateDaysBetweenDates = (startDate: Date, endDate: Date) =>
  dayjs(endDate).diff(dayjs(startDate), 'day');

const getFormatBasedOnMarket = () => {
  return showUsaFeature ? HourFormat.TwelveHour : HourFormat.TwentyFourHour;
};

const GetCurrentUser = () => {
  const { currentUser } = useAppContext();
  return currentUser;
};

const buildTimeFormat = (hourFormat: HourFormat | undefined, timeWithSeconds: boolean) => {
  const definedHourFormat = hourFormat || getFormatBasedOnMarket();
  const isTwelveHourFormat = definedHourFormat === HourFormat.TwelveHour;
  const timeFormat = `${isTwelveHourFormat ? 'hh:mm' : 'HH:mm'}${timeWithSeconds ? ':ss' : ''}${isTwelveHourFormat ? ' A' : ''}`;
  return { timeFormat, isTwelveHourFormat };
};

export const getTimeFormat = (timeWithSeconds = true) => {
  const currentUser = GetCurrentUser();
  return buildTimeFormat(currentUser?.userConfiguration.data.hourFormat, timeWithSeconds);
};

const displayFormattedDate = (
  timestamp: string | number | Date,
  hourFormat: HourFormat | undefined,
  formatWithTime = false,
  timeWithSeconds = true,
) => {
  if (!timestamp) return i18n.t('na_short_label');

  const { timeFormat } = buildTimeFormat(hourFormat, timeWithSeconds);
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return dayjs(date).format(`${generalDateFormat} ${formatWithTime ? timeFormat : ''}`);
};

export const formatDate = ({ timestamp, formatWithTime, timeWithSeconds }: FormatDateProps) => {
  if (!timestamp) return i18n.t('na_short_label');

  const currentUser = GetCurrentUser();
  const hourFormat = currentUser?.userConfiguration.data.hourFormat;

  return displayFormattedDate(timestamp, hourFormat, formatWithTime, timeWithSeconds);
};

export const formatDateHighcharts = (
  timestamp: string | number | Date | undefined,
  hourFormat: HourFormat | undefined,
  formatWithTime = false,
  timeWithSeconds = true,
) => {
  if (!timestamp) return i18n.t('na_short_label');

  return displayFormattedDate(timestamp, hourFormat, formatWithTime, timeWithSeconds);
};

export const formatBirthday = (birthday?: number | string, withAge = false) => {
  if (!birthday) return i18n.t('na_short_label');
  const label = dayjs(birthday).format(generalDateFormat);
  if (!withAge) return label;
  return `${label} (${dayjs().diff(birthday, 'year')})`;
};

export const getNameOfDay = (value: number) => dayjs().day(value).format('dddd');

export const getWeekDaysNames = (startOfWeekDay: number) => {
  const weekdays: string[] = [];
  for (let i = startOfWeekDay; i < startOfWeekDay + 7; i += 1) {
    weekdays.push(getNameOfDay(i));
  }
  return weekdays;
};

export const getEnlistmentKey = (enlistment: Enlistment): string =>
  `${enlistment.groupId}_${dayjs(enlistment.creationTimestamp).valueOf()}`;

export const getEnlistmentDateRange = (enlistment: Enlistment): { startDate: Date; endDate: Date } => ({
  startDate: dayjs(enlistment.creationTimestamp).startOf('day').toDate(),
  endDate: enlistment.expired ? dayjs(enlistment.expiryTimestamp).endOf('day').toDate() : dayjs().endOf('day').toDate(),
});

export const calculateStartTimeFrame = (props: {
  enlistments?: Enlistment[];
  measurementTimestamp?: number;
  clearParameter: () => void;
}) => {
  const { enlistments, measurementTimestamp, clearParameter } = props;
  const endDate = dayjs().endOf('day').toDate();
  const startDate = dayjs(endDate).startOf('day').subtract(2, 'weeks').toDate();

  if (measurementTimestamp) {
    const newEndDate = dayjs(measurementTimestamp).endOf('day').add(10, 'days').toDate();
    clearParameter();
    return {
      startDate: dayjs(measurementTimestamp).startOf('day').subtract(10, 'days').toDate(),
      endDate: newEndDate > endDate ? endDate : newEndDate,
    };
  }

  const lastEnlistment = enlistments?.[0];
  if (lastEnlistment) {
    return getEnlistmentDateRange(lastEnlistment);
  }

  return { endDate, startDate };
};

export const getUserHourFormat = (user?: CurrentUserData): HourFormat => {
  if (user) return user.userConfiguration?.data?.hourFormat;
  return HourFormat.TwentyFourHour;
};

export const getHighchartsDateTimeLabelFormats = (hourFormat: HourFormat | undefined) => {
  const isTwelveHour = (hourFormat ?? getFormatBasedOnMarket()) === HourFormat.TwelveHour;
  return isTwelveHour
    ? { millisecond: '%I:%M:%S %p', second: '%I:%M:%S %p', minute: '%I:%M %p', hour: '%I:%M %p', day: '%e %b' }
    : { millisecond: '%H:%M:%S', second: '%H:%M:%S', minute: '%H:%M', hour: '%H:%M', day: '%e %b' };
};
